import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, BookmarkCheck, Briefcase, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge, inr } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setJob(false));
    if (user?.role === "candidate") {
      api.get("/candidate/saved-jobs").then(({ data }) => {
        setSaved(data.saved_jobs.some((s) => s.job_id === id));
      }).catch(() => {});
    }
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post(`/candidate/saved-jobs/${id}`);
      setSaved(data.saved);
      toast.success(data.saved ? "Job saved" : "Removed from saved jobs");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const apply = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/apply`);
      setApplied(true);
      toast.success("Application submitted successfully");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (job === false)
    return <div className="pt-32 text-center text-muted-foreground">Job not found or no longer published.</div>;
  if (!job) return <div className="pt-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="pt-24 pb-20 min-h-screen" data-testid="job-detail-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/jobs" className="text-sm text-[#2563EB] font-medium inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> All jobs
        </Link>
        <div className="bg-white border border-border/60 rounded-2xl p-8 mt-6">
          <div className="flex flex-wrap justify-between gap-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0B1B3D]">{job.title}</h1>
              <div className="text-muted-foreground mt-2 flex items-center gap-4 flex-wrap text-sm">
                <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {job.company_name}</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {job.location}</span>
                <StatusBadge status={job.work_mode} />
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading font-bold text-xl text-[#0B1B3D]">
                {job.salary_min ? `${inr(job.salary_min)} – ${inr(job.salary_max)}` : "Undisclosed"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{job.salary_type || "annual"}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4 mt-8 border-y border-border/60 py-5 text-sm">
            <div><p className="text-muted-foreground text-xs">Experience</p><p className="font-semibold mt-1">{job.experience_min}–{job.experience_max} yrs</p></div>
            <div><p className="text-muted-foreground text-xs">Qualification</p><p className="font-semibold mt-1">{job.qualification || "—"}</p></div>
            <div><p className="text-muted-foreground text-xs">Vacancies</p><p className="font-semibold mt-1">{job.vacancies}</p></div>
            <div><p className="text-muted-foreground text-xs">Type</p><p className="font-semibold mt-1 capitalize">{(job.employment_type || "").replace(/_/g, " ")}</p></div>
          </div>

          <div className="mt-8 space-y-6">
            <div>
              <h2 className="font-heading font-semibold text-lg text-[#0B1B3D]">About the role</h2>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed whitespace-pre-line">{job.description}</p>
            </div>
            {job.responsibilities && (
              <div>
                <h2 className="font-heading font-semibold text-lg text-[#0B1B3D]">Responsibilities</h2>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed whitespace-pre-line">{job.responsibilities}</p>
              </div>
            )}
            {job.skills?.length > 0 && (
              <div>
                <h2 className="font-heading font-semibold text-lg text-[#0B1B3D]">Skills</h2>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {job.skills.map((s) => (
                    <span key={s} className="text-xs bg-[#2563EB]/8 text-[#2563EB] rounded-full px-3 py-1.5 font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 flex items-center gap-4">
            {applied ? (
              <p className="flex items-center gap-2 text-emerald-600 font-semibold" data-testid="applied-confirmation">
                <CheckCircle2 className="w-5 h-5" /> Application submitted — track it in My Applications.
              </p>
            ) : (
              <Button
                size="lg"
                onClick={apply}
                disabled={busy}
                className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold px-10"
                data-testid="apply-btn"
              >
                {busy ? "Applying…" : user ? "Apply Now" : "Sign in to Apply"}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              onClick={toggleSave}
              className="rounded-full px-6"
              data-testid="save-job-btn"
              aria-label="Save job"
            >
              {saved ? <BookmarkCheck className="w-4 h-4 text-[#F5A623]" /> : <Bookmark className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
